import React, { useState, useEffect } from 'react';
import { supabase } from '../src/lib/supabase';
import { ProjectMeta } from '../types';

interface ProjectDomainSettingsProps {
    project: ProjectMeta;
    onUpdate: () => void;
}

export const ProjectDomainSettings: React.FC<ProjectDomainSettingsProps> = ({ project, onUpdate }) => {
    const [domain, setDomain] = useState('');
    const [loading, setLoading] = useState(false);
    const [checkingDNS, setCheckingDNS] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
    const [currentDomain, setCurrentDomain] = useState<any>(null);

    // Carregar domínio atual do projeto
    useEffect(() => {
        loadProjectDomain();
    }, [project.id]);

    const loadProjectDomain = async () => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('domains')
                .select('*')
                .eq('project_id', project.id)
                .maybeSingle();

            if (error && error.code !== 'PGRST116') throw error;

            if (data) {
                setCurrentDomain(data);
                setDomain(data.hostname);
            } else {
                setCurrentDomain(null);
                setDomain('');
            }
        } catch (error) {
            console.error('Erro ao carregar domínio:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveDomain = async () => {
        if (!domain) return;

        // Validação básica de domínio (suporta subdomínios e TLDs compostos como .com.br)
        const domainRegex = /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
        if (!domainRegex.test(domain)) {
            setMessage({ type: 'error', text: 'Formato de domínio inválido. Ex: quiz.site.com.br' });
            return;
        }

        try {
            setLoading(true);
            setMessage(null);

            // Chamar RPC para salvar domínio
            const { data, error } = await supabase.rpc('set_project_domain', {
                p_project_id: project.id,
                p_hostname: domain.toLowerCase().trim()
            });

            if (error) throw error;

            if (data.success) {
                setMessage({ type: 'success', text: 'Domínio salvo! Agora configure o DNS abaixo.' });
                loadProjectDomain();
                onUpdate();
            } else {
                setMessage({ type: 'error', text: data.error || 'Erro ao salvar domínio' });
            }
        } catch (error) {
            console.error('Erro ao salvar:', error);
            setMessage({ type: 'error', text: `Erro ao salvar: ${error instanceof Error ? error.message : JSON.stringify(error)}` });
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyDNS = async () => {
        if (!currentDomain) return;

        try {
            setCheckingDNS(true);
            setMessage({ type: 'info', text: 'Verificando DNS...' });

            const response = await fetch('/api/verify-dns', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    domain_id: currentDomain.id,
                    hostname: currentDomain.hostname
                })
            });

            const result = await response.json();

            if (result.verified) {
                setMessage({ type: 'success', text: '✅ DNS verificado com sucesso! Seu domínio está ativo.' });
                loadProjectDomain();
            } else {
                setMessage({
                    type: 'error',
                    text: `❌ ${result.message || 'Verificação falhou'}. Aguarde a propagação do DNS.`
                });
            }
        } catch (error) {
            console.error('Erro na verificação:', error);
            setMessage({ type: 'error', text: 'Erro ao verificar DNS.' });
        } finally {
            setCheckingDNS(false);
        }
    };

    const handleRemoveDomain = async () => {
        if (!currentDomain) return;

        if (!confirm('Tem certeza? O acesso pelo domínio personalizado deixará de funcionar.')) return;

        try {
            setLoading(true);
            const { error } = await supabase
                .from('domains')
                .delete()
                .eq('id', currentDomain.id);

            if (error) throw error;

            setMessage({ type: 'success', text: 'Domínio removido com sucesso.' });
            loadProjectDomain();
            onUpdate();
        } catch (error) {
            console.error('Erro ao remover:', error);
            setMessage({ type: 'error', text: 'Erro ao remover domínio.' });
        } finally {
            setLoading(false);
        }
    };

    const isApexDomain = domain.split('.').length === 2;

    if (loading && !currentDomain) {
        return <div className="p-4 text-center">Carregando configurações de domínio...</div>;
    }

    return (
        <div className="bg-white border border-slate-200 rounded-lg p-6 max-w-2xl">
            <h3 className="text-lg font-bold text-slate-800 mb-2">Domínio Personalizado</h3>
            <p className="text-slate-600 mb-6 text-sm">
                Configure um domínio exclusivo (ex: <code>quiz.suamarca.com.br</code>) para os quizzes deste projeto.
            </p>

            {!currentDomain ? (
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                            Domínio ou Subdomínio
                        </label>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={domain}
                                onChange={(e) => setDomain(e.target.value)}
                                placeholder="Ex: quiz.minhaempresa.com"
                                className="flex-1 px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
                            />
                            <button
                                onClick={handleSaveDomain}
                                disabled={loading || !domain}
                                className="px-4 py-2 bg-slate-800 text-white rounded-md hover:bg-slate-700 disabled:opacity-50"
                            >
                                {loading ? 'Salvando...' : 'Adicionar Domínio'}
                            </button>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                            Você pode usar um subdomínio (recomendado) ou um domínio raiz.
                        </p>
                    </div>
                </div>
            ) : (
                <div className="space-y-6">
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                        <div>
                            <div className="flex items-center gap-2">
                                <span className="font-mono text-lg font-semibold text-slate-700">
                                    {currentDomain.hostname}
                                </span>
                                {currentDomain.is_verified ? (
                                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium flex items-center gap-1">
                                        ✓ Ativo
                                    </span>
                                ) : (
                                    <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full font-medium flex items-center gap-1">
                                        ⚠ Pendente
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-slate-500 mt-1">
                                Adicionado em {new Date(currentDomain.created_at).toLocaleDateString()}
                            </p>
                        </div>
                        <button
                            onClick={handleRemoveDomain}
                            className="text-red-600 hover:text-red-700 text-sm font-medium"
                        >
                            Remover
                        </button>
                    </div>

                    {!currentDomain.is_verified && (
                        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                            <h4 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
                                📡 Configuração DNS Necessária
                            </h4>
                            <p className="text-sm text-blue-800 mb-4">
                                Para que seu domínio funcione, acesse o painel do seu provedor de domínio (GoDaddy, Registro.br, Cloudflare) e adicione o seguinte registro:
                            </p>

                            <div className="bg-white p-3 rounded border border-blue-200 font-mono text-sm overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead>
                                        <tr className="text-slate-400 border-b border-slate-100">
                                            <th className="pb-2">Tipo</th>
                                            <th className="pb-2 pl-4">Nome</th>
                                            <th className="pb-2 pl-4">Valor</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td className="pt-2 font-bold text-slate-700 align-top">
                                                {isApexDomain ? 'A' : 'CNAME'}
                                            </td>
                                            <td className="pt-2 pl-4 text-slate-700 align-top">
                                                <div className="flex items-center gap-2 group">
                                                    <span className="font-mono bg-slate-100 px-1 rounded">{isApexDomain ? '@' : domain.split('.')[0]}</span>
                                                    <button
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(isApexDomain ? '@' : domain.split('.')[0]);
                                                            setMessage({ type: 'success', text: 'Copiado!' });
                                                            setTimeout(() => setMessage(null), 2000);
                                                        }}
                                                        className="text-slate-400 hover:text-blue-600 transition-colors p-1"
                                                        title="Copiar Nome"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                                                    </button>
                                                </div>
                                                {!isApexDomain && <div className="text-slate-400 text-xs mt-1">Este é o seu subdomínio</div>}
                                            </td>
                                            <td className="pt-2 pl-4 text-slate-700 align-top">
                                                <div className="flex items-center gap-2 group">
                                                    <span className="font-mono bg-slate-100 px-1 rounded">{isApexDomain ? '76.76.21.21' : 'fbcc3112eb5212a9.vercel-dns-017.com'}</span>
                                                    <button
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(isApexDomain ? '76.76.21.21' : 'fbcc3112eb5212a9.vercel-dns-017.com');
                                                            setMessage({ type: 'success', text: 'Copiado!' });
                                                            setTimeout(() => setMessage(null), 2000);
                                                        }}
                                                        className="text-slate-400 hover:text-blue-600 transition-colors p-1"
                                                        title="Copiar Valor"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div className="mt-4 flex justify-end">
                                <button
                                    onClick={handleVerifyDNS}
                                    disabled={checkingDNS}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                                >
                                    {checkingDNS ? (
                                        <>
                                            <span className="animate-spin">⟳</span> Verificando...
                                        </>
                                    ) : (
                                        <>🔎 Verificar Configuração</>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {message && (
                <div className={`mt-4 p-3 rounded-md text-sm ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' :
                    message.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' :
                        'bg-blue-50 text-blue-700 border border-blue-200'
                    }`}>
                    {message.text}
                </div>
            )}
        </div>
    );
};
